import { createFileRoute } from "@tanstack/react-router";
import { ArrowUpRight, Check } from "lucide-react";
import { ClosingCTA } from "@/components/landing/ClosingCTA";
import { FaqSection, faqSchemaScript, type FaqItem } from "@/components/landing/FaqSection";
import { Footer } from "@/components/landing/Footer";
import { Header } from "@/components/landing/Header";
import { PageIntro } from "@/components/landing/PageIntro";
import { ParticleBackground } from "@/components/landing/ParticleBackground";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { RelatedServices } from "@/components/landing/RelatedServices";
import gatorFeatureAsset from "@/assets/gatorguard-imac.png.asset.json";
import gatorDevicesAsset from "@/assets/gatorguard-responsive-devices.png.asset.json";
import { freshnessMeta, webPageSchemaScript } from "@/lib/page-freshness";
import { serviceSchemaScripts } from "@/lib/service-schema";

const PUBLISHED = "2026-10-03T00:00:00+00:00";
const MODIFIED = "2026-10-03T00:00:00+00:00";
const TITLE = "Interactive Product Visualizers for Lead Generation | Square Migration";
const DESCRIPTION = "Help buyers preview products, finishes and transformations with a custom interactive visualizer that delivers a personalized result and captures qualified leads.";

const FAQS: FaqItem[] = [
  {
    question: "What can an interactive visualizer show?",
    answer: "Visualizers can help buyers compare finishes, colors, layouts, products or before-and-after transformations. The experience is built around the decision your customer needs to make, not a generic template.",
  },
  {
    question: "Can customers use their own photo?",
    answer: "Yes. A visualizer can let customers upload a photo of their room, floor, building or product and preview available options in a more personal context.",
  },
  {
    question: "How does a visualizer generate leads?",
    answer: "The buyer receives useful value first, then can save their mockup, request a quote or share their preferred option. The submitted image, selections and result give sales useful context for follow-up.",
  },
  {
    question: "Will the visualizer work on mobile devices?",
    answer: "Yes. We design visualizers for phones, tablets and desktop screens so buyers can complete the experience wherever they discover it.",
  },
];

const CAPABILITIES = [
  "Upload a customer photo",
  "Compare products or finishes",
  "Show a clear before-and-after view",
  "Save a personalized mockup",
  "Capture selections with the lead",
  "Move naturally into a quote request",
];

export const Route = createFileRoute("/visualizers")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://www.squaremigration.com/visualizers" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://www.squaremigration.com/og-image.jpg" },
      { name: "twitter:image", content: "https://www.squaremigration.com/og-image.jpg" },
      ...freshnessMeta({ published: PUBLISHED, modified: MODIFIED }),
    ],
    links: [{ rel: "canonical", href: "https://www.squaremigration.com/visualizers" }],
    scripts: [
      ...serviceSchemaScripts({ path: "/visualizers", name: "Interactive Visualizers", description: DESCRIPTION, serviceType: "Interactive product visualizer development" }),
      faqSchemaScript(FAQS),
      webPageSchemaScript({ path: "/visualizers", name: TITLE, description: DESCRIPTION, published: PUBLISHED, modified: MODIFIED }),
    ],
  }),
  component: VisualizersPage,
});

function VisualizersPage() {
  return (
    <main className="relative">
      <ParticleBackground />
      <Header />
      <PageIntro
        eyebrow="Interactive Visualizers"
        title="Let buyers see the outcome before they make the decision."
        description="Custom visualizers turn product choices into a hands-on experience. Buyers can explore options, create a personalized result and arrive at the sales conversation with a clear preference."
        bullets={["Product & finish previews", "Customer photo uploads", "Before-and-after comparisons", "Saved personalized mockups"]}
      />

      <section className="border-t border-border/50 bg-background/95 px-6 py-16 md:px-8 md:py-24 lg:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="mb-12 grid items-end gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
            <div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.22em] text-primary">Featured Project</p>
              <h2 className="text-3xl font-semibold leading-tight sm:text-4xl">GatorGuard Concrete Coatings <span className="text-primary">Visualizer</span></h2>
            </div>
            <p className="leading-relaxed text-muted-foreground lg:text-lg">A branded experience that helps homeowners picture a new floor using their own space, compare coating styles and save a personalized mockup before requesting a quote.</p>
          </div>

          <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
            <div className="relative min-h-[320px] sm:min-h-[470px]">
              <img src={gatorFeatureAsset.url} alt="GatorGuard concrete coating visualizer shown on an iMac" className="absolute inset-0 h-full w-full object-contain" />
            </div>
            <div>
              <p className="mb-6 text-lg leading-relaxed text-foreground/90">The visualizer gives shoppers a reason to engage before speaking with sales. They upload a photo, select a finish, compare the transformation and carry their preferred result into the next conversation.</p>
              <ul className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                {CAPABILITIES.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary"><Check className="h-3.5 w-3.5" aria-hidden="true" /></span>
                    {item}
                  </li>
                ))}
              </ul>
              <a href="https://visualizer.mygatorguard.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-primary/60 px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-primary/10">
                Explore the GatorGuard Visualizer <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </div>

          <div className="relative mt-14 overflow-hidden rounded-lg border border-border/60 bg-card/50 px-5 py-8 shadow-2xl md:mt-20 md:px-10 md:py-12 lg:px-14 lg:py-14">
            <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />
            <div className="pointer-events-none absolute -bottom-28 -right-20 h-80 w-80 rounded-full bg-primary/5 blur-3xl" aria-hidden="true" />

            <div className="relative z-10 grid items-center gap-10 lg:grid-cols-[0.72fr_1.28fr] lg:gap-12">
              <div>
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.22em] text-primary">Responsive by Design</p>
                <h3 className="mb-4 text-2xl font-semibold leading-tight sm:text-3xl">One useful experience, on every screen.</h3>
                <p className="leading-relaxed text-muted-foreground">Homeowners can upload a photo, compare finishes and save their preferred result whether they start on a phone, tablet or desktop.</p>
                <div className="mt-7 flex items-center gap-3 text-xs font-semibold uppercase text-muted-foreground">
                  <span className="h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
                  Live customer experience
                </div>
              </div>

              <div className="relative flex min-h-[280px] items-center justify-center sm:min-h-[390px]">
                <div className="absolute bottom-[12%] left-1/2 h-10 w-4/5 -translate-x-1/2 rounded-full bg-primary/10 blur-2xl" aria-hidden="true" />
                <img src={gatorDevicesAsset.url} alt="GatorGuard floor visualizer shown across desktop, laptop and tablet screens" className="relative h-auto w-full max-w-3xl object-contain transition-transform duration-700 hover:scale-[1.02]" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <ProcessSection
        eyebrow="How It Works"
        title="Turn a hard-to-picture decision into a clear next step."
        lead="We shape the experience around the product choice your buyer is struggling to make, then connect the personalized result to your sales process."
        steps={[
          { number: "01", title: "Choose the Decision", description: "Identify the product, finish or transformation buyers need help imagining before they move forward." },
          { number: "02", title: "Map the Experience", description: "Define the photo, options, comparison and result that will make the decision feel easier." },
          { number: "03", title: "Build & Brand It", description: "Create a fast, mobile-friendly visualizer that feels like a natural extension of your website." },
          { number: "04", title: "Connect the Lead", description: "Route the buyer's image, selections and saved result into your quote or follow-up process." },
        ]}
      />

      <ClosingCTA
        eyebrow="Make the Outcome Visible"
        headline={<>What do your buyers need to <span className="text-primary">see before they say yes?</span></>}
        subhead="That’s the visualizer."
        body="Bring the product choices, finishes or transformation you sell. We’ll map the simplest useful experience and show you how it can create better-qualified inquiries."
        ctaLabel="Talk Through a Visualizer"
      />
      <FaqSection items={FAQS} />
      <RelatedServices current="/visualizers" />
      <Footer />
    </main>
  );
}